import React, { useEffect, useState } from 'react';
import { api } from '../../api/client';
import Switch from './Switch';

import { ta, taf, adminLocale } from '../../i18n/adminT';
export default function DeviceAuthSettings() {
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  const [devices, setDevices] = useState([]);
  const [generatedCode, setGeneratedCode] = useState(null); // { code, expiresAt }
  const [generating, setGenerating] = useState(false);
  const [renamingId, setRenamingId] = useState(null);
  const [renameDraft, setRenameDraft] = useState('');
  const [revokingId, setRevokingId] = useState(null);
  const [readerSavingId, setReaderSavingId] = useState(null);

  const loadConfig = async () => {
    try {
      const res = await api.getDeviceAuthConfig();
      setEnabled(Boolean(res?.data?.enabled));
    } catch {
      // in caso di errore resta disattivato
    }
  };

  const loadDevices = async () => {
    try {
      const res = await api.getAuthorizedDevices();
      setDevices(res.data);
    } catch {
      // in caso di errore l'elenco resta vuoto
    }
  };

  useEffect(() => {
    Promise.all([loadConfig(), loadDevices()]).finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setError('');
    setSaved(false);
    setSaving(true);
    try {
      await api.saveDeviceAuthConfig({ enabled });
      setSaved(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateCode = async () => {
    setError('');
    setGeneratedCode(null);
    setGenerating(true);
    try {
      const res = await api.generateDeviceCode();
      setGeneratedCode(res);
    } catch (err) {
      setError(err.message);
    } finally {
      setGenerating(false);
    }
  };

  const startRename = (device) => {
    setRenamingId(device.id);
    setRenameDraft(device.label);
  };

  const handleRenameSave = async (id) => {
    setError('');
    try {
      await api.renameAuthorizedDevice(id, renameDraft);
      setRenamingId(null);
      await loadDevices();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleToggleReader = async (device) => {
    setError('');
    setReaderSavingId(device.id);
    try {
      await api.setDeviceNfcReader(device.id, !device.nfc_reader);
      await loadDevices();
    } catch (err) {
      setError(err.message);
    } finally {
      setReaderSavingId(null);
    }
  };

  const handleRevoke = async (device) => {
    if (!window.confirm(taf('Revocare l\'accesso a "{label}"?\n\nQuel dispositivo dovrà essere riattivato con un nuovo codice per tornare a mostrare le pagine pubbliche.', { label: device.label }))) return;
    setError('');
    setRevokingId(device.id);
    try {
      await api.revokeAuthorizedDevice(device.id);
      await loadDevices();
    } catch (err) {
      setError(err.message);
    } finally {
      setRevokingId(null);
    }
  };

  if (loading) return null;

  return (
    <div>
      <h1 className="admin-page-title">{ta('Dispositivi autorizzati')}</h1>
      <p className="admin-page-subtitle">
        {ta('Controlla quali dispositivi possono mostrare le pagine pubbliche (kiosk e contatti), così un tablet o un computer qualunque sulla rete non può aprirle senza essere stato autorizzato prima.')}
      </p>

      <div className="admin-card" style={{ maxWidth: 480 }}>
        {error && <div className="form-error">{error}</div>}
        {saved && (
          <div style={{ background: 'rgba(62,142,126,0.12)', color: 'var(--teal-green-dark)', borderRadius: 'var(--radius-control)', padding: '10px 14px', fontSize: '0.85rem', marginBottom: 16 }}>
            {ta('Impostazioni salvate.')}
          </div>
        )}

        <label className="checkbox-row">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => { setEnabled(e.target.checked); setSaved(false); }}
          />
          <span>{ta('Attiva il controllo dispositivi')}</span>
        </label>

        <p style={{ fontSize: '0.78rem', color: 'var(--muted-text)', marginTop: -4, marginBottom: 16 }}>
          {ta('⚠ Prima di attivarlo, autorizza almeno un dispositivo qui sotto: altrimenti anche i tablet che usi già oggi smetteranno di mostrare le pagine finché non li attivi con un codice.')}
        </p>

        <button
          type="button"
          className="btn btn-primary"
          onClick={handleSave}
          disabled={saving}
          style={{ width: 'auto', padding: '10px 20px' }}
        >
          {saving ? ta('Salvataggio…') : ta('Salva')}
        </button>
      </div>

      <div className="admin-card" style={{ maxWidth: 480 }}>
        <h3 style={{ marginBottom: 10 }}>{ta('Autorizza un nuovo dispositivo')}</h3>
        <p style={{ fontSize: '0.85rem', color: 'var(--muted-text)', marginBottom: 14 }}>
          {ta('Genera un codice, poi inseriscilo sul dispositivo da autorizzare: comparirà da solo la schermata di attivazione se il controllo è attivo. Il codice è valido 15 minuti e si può usare una sola volta.')}
        </p>
        <p style={{ fontSize: '0.85rem', color: 'var(--muted-text)', marginBottom: 14 }}>
          {ta('Se il controllo è spento la schermata non compare: in quel caso apri sul dispositivo l\'indirizzo')} <span className="mono">{window.location.origin}{ta('/attiva')}</span> {ta('e inserisci lì il codice. Serve anche per indicare quali dispositivi hanno il lettore badge (colonna qui sotto), perché solo un dispositivo attivato viene riconosciuto.')}
        </p>

        {generatedCode && (
          <div style={{ background: 'var(--paper-dim)', borderRadius: 'var(--radius-control)', padding: '14px 16px', marginBottom: 16, textAlign: 'center' }}>
            <div className="mono" style={{ fontSize: '1.6rem', fontWeight: 700, letterSpacing: '0.1em', color: 'var(--ink-navy)' }}>
              {generatedCode.code}
            </div>
            <div style={{ fontSize: '0.78rem', color: 'var(--muted-text)', marginTop: 6 }}>
              {ta('Valido fino alle')} {new Date(generatedCode.expiresAt).toLocaleTimeString(adminLocale(), { hour: '2-digit', minute: '2-digit' })}
            </div>
          </div>
        )}

        <button
          type="button"
          className="btn btn-primary"
          onClick={handleGenerateCode}
          disabled={generating}
          style={{ width: 'auto', padding: '10px 20px' }}
        >
          {generating ? ta('Generazione…') : ta('Genera codice')}
        </button>
      </div>

      <div className="admin-card">
        <h3 style={{ marginBottom: 14 }}>{ta('Dispositivi attivati')}</h3>
        <table className="admin-table">
          <thead>
            <tr>
              <th>{ta('Nome dispositivo')}</th>
              <th>{ta('Attivato il')}</th>
              <th>{ta('Ultimo utilizzo')}</th>
              <th title={ta('Il dispositivo ha un lettore badge NFC collegato')}>{ta('Lettore badge')}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {devices.map((d) => (
              <tr key={d.id}>
                <td>
                  {renamingId === d.id ? (
                    <input
                      type="text"
                      value={renameDraft}
                      onChange={(e) => setRenameDraft(e.target.value)}
                      style={{ padding: '5px 8px', borderRadius: 'var(--radius-control)', border: '1.5px solid var(--border-hair)', fontSize: '0.9rem' }}
                      autoFocus
                    />
                  ) : d.label}
                </td>
                <td className="mono">{new Date(d.created_at).toLocaleDateString(adminLocale())}</td>
                <td className="mono">{d.last_seen_at ? new Date(d.last_seen_at).toLocaleString(adminLocale()) : '—'}</td>
                <td>
                  <Switch
                    checked={Boolean(d.nfc_reader)}
                    onChange={() => handleToggleReader(d)}
                    disabled={readerSavingId === d.id}
                    label={taf('Lettore badge su {label}', { label: d.label })}
                    onText={ta('Attivo')}
                    offText={ta('No')}
                  />
                </td>
                <td style={{ display: 'flex', gap: 6 }}>
                  {renamingId === d.id ? (
                    <>
                      <button className="btn-ghost btn" style={{ width: 'auto', padding: '6px 10px' }} onClick={() => handleRenameSave(d.id)}>{ta('Salva')}</button>
                      <button className="btn-ghost btn" style={{ width: 'auto', padding: '6px 10px' }} onClick={() => setRenamingId(null)}>{ta('Annulla')}</button>
                    </>
                  ) : (
                    <>
                      <button className="btn-ghost btn" style={{ width: 'auto', padding: '6px 10px' }} onClick={() => startRename(d)}>{ta('Rinomina')}</button>
                      <button
                        className="btn-ghost btn"
                        style={{ width: 'auto', padding: '6px 10px', color: 'var(--clay-red-dark)' }}
                        onClick={() => handleRevoke(d)}
                        disabled={revokingId === d.id}
                      >
                        {revokingId === d.id ? ta('Revoca…') : ta('Revoca')}
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {devices.length === 0 && <p style={{ color: 'var(--muted-text)', padding: '16px 0 0' }}>{ta('Nessun dispositivo attivato.')}</p>}
      </div>
    </div>
  );
}
